import React, { useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Download } from 'lucide-react';
import { saveAs } from 'file-saver';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { buildLaiPatientReportWorkbook, type LaiPatientReportRow } from './laiPatientReportWorkbook';

const REPORT = gql`query LaiPatientListReport($nextDue: String!) { laiPatientListReport(nextDue: $nextDue) { id cells { key value } } }`;

export const LaiPatientReportDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const [nextDue, setNextDue] = useState('');
  const [password, setPassword] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [load, { loading, error }] = useLazyQuery<{ laiPatientListReport: LaiPatientReportRow[] }>(REPORT, { fetchPolicy: 'network-only' });
  const busy = loading || preparing;
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && busy) return;
    onOpenChange(nextOpen);
  };
  const download = async () => {
    setDownloadError('');
    setPreparing(true);
    try {
      const result = await load({ variables: { nextDue } });
      if (!result.data || result.error) {
        setDownloadError('The patient report could not be downloaded. Please try again.');
        return;
      }
      const bytes = await buildLaiPatientReportWorkbook(result.data.laiPatientListReport, password);
      saveAs(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${nextDue.slice(5, 7)}.${nextDue.slice(8, 10)}.${nextDue.slice(0, 4)} LAI Hearings List.xlsx`);
      onOpenChange(false);
    } catch {
      setDownloadError('The patient report could not be downloaded. Please try again.');
    } finally {
      setPreparing(false);
    }
  };
  return <Dialog open={open} onOpenChange={handleOpenChange}>
    <DialogContent className="max-w-lg" onPointerDownOutside={(event) => event.preventDefault()}>
      <DialogHeader><DialogTitle>Patient List Report</DialogTitle></DialogHeader>
      <label htmlFor="lai-report-next-due">Next Due</label>
      <Input id="lai-report-next-due" type="date" required value={nextDue} onChange={(event) => setNextDue(event.target.value)} />
      <label htmlFor="lai-report-password">Workbook protection password (optional)</label>
      <Input id="lai-report-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
      {(downloadError || error) && <p role="alert" className="text-[var(--semantic-error)]">{downloadError || 'The patient report could not be downloaded. Please try again.'}</p>}
      <div className="flex justify-end gap-2">
        <Button id="lai-report-download" disabled={!nextDue || loading || preparing} onClick={() => { void download(); }}><Download className="h-4 w-4" />{loading || preparing ? 'Preparing…' : 'Download'}</Button>
        <Button id="lai-report-close" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>Close</Button>
      </div>
    </DialogContent>
  </Dialog>;
};
